import { useAppSelector } from '../store/hooks';

/**
 * Exposes helper methods to check what the logged-in user is allowed to do,
 * based on the `permissions` array returned by the login API.
 *
 * Permission string formats:
 *   "Module.SubModule.action"  – e.g. "Dashboard.Activity.read"
 *   "Module.FullAccess"        – e.g. "Teams.FullAccess"
 */
export const usePermissions = () => {
  const user = useAppSelector((state) => state.auth.user);
  const permissions: string[] = user.permissions;
  const isSuperAdmin = user.isSuperAdmin;

  /**
   * Returns true if the user has at least one permission whose module prefix
   * matches `module` (case-insensitive).
   *
   * Used for sidebar visibility — hide item when false.
   */
  const hasModuleAccess = (module: string | undefined): boolean => {
    if (isSuperAdmin) return true;
    if (!module) return true; // no restriction defined → always show
    const prefix = module.toLowerCase();
    return permissions.some((p) => p.toLowerCase().startsWith(prefix + '.'));
  };

  /**
   * Returns true when the user can perform `action` on `Module.SubModule`.
   *
   * Matches either:
   *   - exact  "Module.SubModule.action"
   *   - wildcard "Module.FullAccess"
   *
   * Used for button disabled state — disable when false.
   */
  const hasAction = (module: string, subModule: string, action: string): boolean => {
    if (isSuperAdmin) return true;
    const exact = `${module}.${subModule}.${action}`.toLowerCase();
    const fullAccess = `${module}.FullAccess`.toLowerCase();
    return permissions.some((p) => p.toLowerCase() === exact || p.toLowerCase() === fullAccess);
  };

  /**
   * Convenience: true when the module has FullAccess.
   */
  const hasFullAccess = (module: string): boolean => {
    if (isSuperAdmin) return true;
    const fullAccess = `${module}.FullAccess`.toLowerCase();
    return permissions.some((p) => p.toLowerCase() === fullAccess);
  };

  return { hasModuleAccess, hasAction, hasFullAccess, permissions, isSuperAdmin };
};
